// Estimates screenplay runtime from chapter markdown. Mirrors the line walk in
// `renderScreenplay` (lib/pdf.js) so the page count matches the exported PDF.
// Industry convention: 1 page of properly formatted screenplay ≈ 1 minute.

const CM = 28.3465;
const PAGE_W = 612;
const PAGE_H = 792;
const MG = { left: 3.8 * CM, right: 2.5 * CM, top: 2.5 * CM, bottom: 2.5 * CM };
const BODY_W = PAGE_W - MG.left - MG.right;
const LINE_H = 14.4;
const CHAR_W = 7.2; // Courier 12pt
const USABLE = PAGE_H - MG.top - MG.bottom;

const X_CHAR = 10.0 * CM;
const X_DIA = 7.5 * CM;
const X_PAR = 7.5 * CM;
const W_DIA = PAGE_W - X_DIA - 5.0 * CM;
const W_PAR = PAGE_W - X_PAR - 5.0 * CM;
const W_CHR = PAGE_W - X_CHAR - MG.right;

const wrapCount = (text, maxW) => {
  if (!text) return 1;
  return Math.max(1, Math.ceil((text.length * CHAR_W) / maxW));
};

function stripHidden(text) {
  if (!text) return '';
  return text.replace(
    /\[\[hidden\s+([^\]]+?)\]\](?:([\s\S]*?)\[\[\/hidden\]\])?/g,
    (_m, attrs, body) => {
      const a = String(attrs);
      if (/\bredacted\b/.test(a)) return '';
      const vis = /\bvisible=([^\s\]]*)/.exec(a);
      const aud = vis ? vis[1].split(',').map((s) => s.trim()).filter(Boolean) : [];
      return aud.length === 0 ? '' : body || '';
    },
  );
}

function measureBody(content) {
  const cleaned = stripHidden(content || '');
  const lines = cleaned.split(/\r?\n/);
  let y = 0;

  for (const raw of lines) {
    const line = raw.replace(/\s+$/g, '');
    if (!line.trim()) { y += LINE_H * 0.6; continue; }
    if (/^@@(CHR|DLG|PAR|TRN)\s*$/i.test(line.trim())) continue;

    if (/^#\s+/.test(line)) {
      const heading = line.replace(/^#+\s+/, '');
      y += LINE_H * 0.6;
      y += wrapCount(heading, BODY_W) * LINE_H;
      y += LINE_H * 0.4;
      continue;
    }
    // Inline <char>NAME</char> alone in a line → character cue at X_CHAR.
    const aloneChar = line.trim().match(/^<char>\s*([^<]+?)\s*<\/char>$/i);
    if (aloneChar) {
      y += LINE_H * 0.6;
      y += wrapCount(aloneChar[1].trim(), W_CHR) * LINE_H;
      continue;
    }
    const chr = line.match(/^@@CHR\s+(.+)$/i);
    if (chr) {
      y += LINE_H * 0.6;
      y += wrapCount(chr[1].trim(), W_CHR) * LINE_H;
      continue;
    }
    const par = line.match(/^@@PAR\s+(.+)$/i);
    if (par) {
      let body = par[1].trim();
      if (!/^\(.*\)$/.test(body)) body = `(${body})`;
      y += wrapCount(body, W_PAR) * LINE_H;
      continue;
    }
    const dlg = line.match(/^@@DLG\s+(.+)$/i);
    if (dlg) {
      const body = dlg[1].trim().replace(/<char>\s*([^<]+?)\s*<\/char>/gi, (_, n) => n.trim().toUpperCase());
      y += wrapCount(body, W_DIA) * LINE_H;
      y += LINE_H * 0.2;
      continue;
    }
    const trn = line.match(/^@@TRN\s+(.+)$/i);
    if (trn) {
      y += LINE_H * 0.6;
      y += wrapCount(trn[1].trim(), BODY_W) * LINE_H;
      y += LINE_H * 0.4;
      continue;
    }
    const plain = line
      .replace(/<char>\s*([^<]+?)\s*<\/char>/gi, (_, n) => n.trim().toUpperCase())
      .replace(/@@(CHR|DLG|PAR|TRN)\b\s*/gi, '')
      .replace(/[*_`~]/g, '');
    if (!plain.trim()) continue;
    y += wrapCount(plain, BODY_W) * LINE_H;
  }
  return y;
}

// Each scene starts on a fresh PDF page. Title block ≈ titleLines * 18 + 12pt.
export function estimateScenePages(content, title = '') {
  const titleLines = title ? wrapCount(title, BODY_W) : 1;
  const titleH = titleLines * 18 + 12;
  const used = titleH + measureBody(content);
  return Math.max(1, Math.ceil(used / USABLE));
}

export function estimateTotalPages(scenes) {
  if (!scenes || scenes.length === 0) return 0;
  return scenes.reduce(
    (sum, s) => sum + estimateScenePages(s.content, s.title),
    0,
  );
}

// Continuous-flow estimator for plain screenplays. Concatenates each scene's
// heading + body into one virtual stream so we get the true on-page footprint
// (scenes don't break to a new page in the plain renderer). Returns total pt
// height; divide by `PAGE_USABLE_PT` for pages.
export function measureContinuousPt(scenes) {
  if (!scenes || scenes.length === 0) return 0;
  const joined = scenes
    .map((s) => {
      const heading = s.title ? `@@SCN ${s.title}` : '';
      return heading ? `${heading}\n${s.content || ''}` : (s.content || '');
    })
    .join('\n\n');
  return measureBody(joined);
}

// Per-scene pt height in the same continuous-flow geometry. Useful for
// weighting how much a single scene contributes to the total runtime.
export function measureScenePt(content, title = '') {
  const heading = title ? `@@SCN ${title}` : '';
  const stream = heading ? `${heading}\n${content || ''}` : (content || '');
  return measureBody(stream);
}

export const PAGE_USABLE_PT = USABLE;

// Pages → "1min" / "34min" / "1h46min" / "2h".
export function formatRuntime(pages) {
  if (!pages || pages <= 0) return '0min';
  const mins = Math.max(1, Math.round(pages));
  if (mins < 60) return `${mins}min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (m === 0) return `${h}h`;
  return `${h}h${String(m).padStart(2, '0')}min`;
}
